import { create } from 'zustand';
import { User } from '../types';
import authService from '../services/auth.service';
import { useNotificationStore } from './notificationStore';
import { subscribeToPush } from '../services/pushService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => void;
  refreshProfile: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const { user, token } = await authService.login({ username, password });
      set({ user, isAuthenticated: true, isLoading: false });
      useNotificationStore.getState().connect(token);
      subscribeToPush().catch(() => {});
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Erreur de connexion', 
        isLoading: false 
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    useNotificationStore.getState().disconnect();
    try {
      await authService.logout();
    } finally {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  checkAuth: () => {
    const user = authService.getCurrentUser();
    const isAuthenticated = authService.isAuthenticated();
    set({ user, isAuthenticated });
    if (isAuthenticated) {
      const token = localStorage.getItem('token');
      if (token) {
        useNotificationStore.getState().connect(token);
        subscribeToPush().catch(() => {});
      }
    }
  },

  refreshProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.getProfile();
      localStorage.setItem('user', JSON.stringify(user));
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors du chargement du profil',
        isLoading: false
      });
      throw error;
    }
  },

  updateProfile: async (data: Partial<User>) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.updateProfile(data);
      set({ user, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Erreur lors de la mise à jour du profil',
        isLoading: false
      });
      throw error;
    }
  },
}));